import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	DocumentSettingsComponent,
	DocumentSettingsDialogData,
	DocumentSettingsSuccessData,
	defaultDocumentSettingsDialogConfig,
} from '@components/documents-matter-editor/document-settings/document-settings.component';
import { ICON } from '@core/util/icon';
import {
	BusinessDocumentDefaults,
	BusinessSettings,
	BusinessTrustLedgerSettings,
} from '@models/businessSettings/settings';
import { SignatureHeight } from '@models/businesses';
import {
	LongDateFormat,
	ShortDateFormat,
} from '@models/businessSettings/businessFormatTypes';
import { Font } from '@models/font';
import { DocumentMargins, MarginUnit, PageSize } from '@models/documents';
import { Select } from '@ngxs/store';
import { BusinessDispatch, BusinessState } from '@state/business';
import { Observable } from 'rxjs';
import {
	DropdownOption,
	DropdownResult,
	SelectionGroup,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import {
	FontDropdownOptions,
	FontLineHeightDropdownOptions,
	FontSizeDropdownOptions,
	LongDateOptions,
	ShortDateOptions,
	SignatureHeightDropdownOptions,
} from '../../finances/business-finances-general/business-finances-general.types';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { recalculateMargins } from '@core/util/number';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FontInfo } from '@core/util/font';
import {
	DateToBusinessLongDate,
	DateToBusinessShortDate,
} from '@core/util/datetime';
import { PillModes } from 'src/app/shared/atomic/atoms/pill/pill.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import {
	BusinessBasedDocxDrawerData,
	BusinessBaseDocxDrawerComponent,
} from './business-base-docxs-table/business-base-docx-drawer/business-base-docx-drawer.component';
import { BusinessSettingsVisibleTemplates } from '@models/documents';
import { TestHooks } from './formatting.hooks';

@UntilDestroy()
@Component({
	selector: 'app-business-document-formatting',
	templateUrl: './formatting.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class DocumentFormattingComponent implements OnInit {
	public ICON = ICON;
	public testHooks = TestHooks;
	public defaultFont: string;
	public defaultPageMargins$: Observable<DocumentMargins>;
	public fontDropdownOptions = FontDropdownOptions;
	public fontSizeDropdownOptions = FontSizeDropdownOptions;
	public fontLineHeightDropdownOptions = FontLineHeightDropdownOptions;
	public pageLayoutHeadingText = 'Page layout';
	public settings: BusinessSettings;
	public selectedLongDateFormat: string;
	public selectedShortDateFormat: string;
	public shortDateGroup: SelectionGroup[] = [{ items: ShortDateOptions }];
	public longDateGroup: SelectionGroup[] = [{ items: LongDateOptions }];
	public PillModes = PillModes;
	public signatureHeightDropdownOptions = SignatureHeightDropdownOptions;

	@Select(BusinessState.getDefaultFont) defaultFont$: Observable<Font>;
	@Select(BusinessState.getDefaultFontSize)
	defaultFontSize$: Observable<number>;
	@Select(BusinessState.getDefaultLineHeight)
	defaultLineHeight$: Observable<number>;
	@Select(BusinessState.getDefaultPageSize)
	defaultPageSize$: Observable<PageSize>;
	@Select(BusinessState.getDefaultPageDialogData)
	defaultPageDialogData$: Observable<DocumentSettingsDialogData>;
	@Select(BusinessState.getDefaultSettings)
	settings$: Observable<BusinessSettings>;
	@Select(BusinessState.getDefaultSignatureHeight)
	defaultSignatureHeight$: Observable<SignatureHeight>;
	@Select(BusinessState.getDefaultPageMargins)
	_defaultPageMargins$: Observable<DocumentMargins>;
	@Select(BusinessState.useHtmlDocuments)
	useHtmlDocuments$: Observable<boolean>;
	@Select(BusinessState.getVisibleTemplates)
	visibleTemplates$: Observable<BusinessSettingsVisibleTemplates>;

	public BusinessSettingsVisibleTemplates = BusinessSettingsVisibleTemplates;

	constructor(
		private businessDispatch: BusinessDispatch,
		private dialog: MatDialog,
		private drawerService: DrawerService,
	) {}

	ngOnInit(): void {
		this.defaultPageMargins$ = this._defaultPageMargins$.pipe(
			map((margins) =>
				recalculateMargins(MarginUnit.POINT, margins.unit, margins),
			),
		);

		this.defaultFont$.pipe(untilDestroyed(this)).subscribe((font) => {
			this.defaultFont = FontInfo[font].title;
		});

		const currentYear = new Date().getFullYear();

		const previewDate = new Date(currentYear, 1, 1);

		this.settings$.pipe(untilDestroyed(this)).subscribe((_settings) => {
			this.settings = _settings;
			if (_settings) {
				this.selectedShortDateFormat = DateToBusinessShortDate(
					previewDate,
					_settings.shortDateFormat,
				);
				this.selectedLongDateFormat = DateToBusinessLongDate(
					previewDate,
					_settings.longDateFormat,
				);
			}
		});
	}

	addBaseDocx(): void {
		this.drawerService.openDrawer(BusinessBaseDocxDrawerComponent, {
			title: 'Add New Letterhead & Footer',
			data: {
				baseDocx: {
					overrides: {},
					path: null,
					title: '',
					type: null,
				},
			} as BusinessBasedDocxDrawerData,
			size: 'sm',
		});
	}

	setDocumentWitness($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			documents: { singleWitnessSignature: $event } as BusinessDocumentDefaults,
		});
	}

	setSelectedFont($event: DropdownResult<DropdownOption<Font>>): void {
		this.businessDispatch.SetDefaultFont($event.result.value);
	}

	setSelectedFontSize($event: DropdownResult<DropdownOption<number>>): void {
		this.businessDispatch.SetDefaultFontSize($event.result.value);
	}

	setSelectedFontLineHeight(
		$event: DropdownResult<DropdownOption<number>>,
	): void {
		this.businessDispatch.SetDefaultLineHeight($event.result.value);
	}

	setSelectedSignatureHeight(
		$event: DropdownResult<DropdownOption<SignatureHeight>>,
	): void {
		this.businessDispatch.SetDefaultSignatureHeight($event.result.value);
	}

	setAuthorityLine($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			hideAuthorityLine: $event,
		} as BusinessSettings);
	}

	setHide0Values($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			trustLedger: { hide0Values: $event } as BusinessTrustLedgerSettings,
		});
	}

	setHide0ValuesInStatementOfAccount($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			statementOfAccount: {
				hide0Values: $event,
			} as BusinessTrustLedgerSettings,
		});
	}

	setDefaultMarginsForWordExport($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			documents: {
				useDefaultMarginsForWordExport: $event,
			} as BusinessDocumentDefaults,
		});
	}

	setZebraStripes($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			hideZebraStripes: $event,
		} as BusinessSettings);
	}

	setSublistSingleLineBreaks($event: boolean): void {
		this.businessDispatch.UpdateDefaultSettings({
			sublistSingleLine: $event,
		} as BusinessSettings);
	}

	onLongDateFormatSelect(
		value: DropdownResult<DropdownOption<LongDateFormat>>,
	): void {
		this.businessDispatch.UpdateDefaultSettings({
			longDateFormat: value.result.value,
		});
	}

	onShortDateFormatSelect(
		value: DropdownResult<DropdownOption<ShortDateFormat>>,
	): void {
		this.businessDispatch.UpdateDefaultSettings({
			shortDateFormat: value.result.value,
		});
	}

	/**
	 * launches the Paper size/margin dialog and updates the values on close if applicable
	 */
	editPageSize(): void {
		this.defaultPageDialogData$
			.pipe(
				untilDestroyed(this),
				first(),
				switchMap((data) =>
					this.dialog
						.open<
							DocumentSettingsComponent,
							DocumentSettingsDialogData,
							DocumentSettingsSuccessData
						>(DocumentSettingsComponent, {
							...defaultDocumentSettingsDialogConfig,
							data: {
								...data,
								headingText: this.pageLayoutHeadingText,
							},
						})
						.afterClosed(),
				),
				filter((data) => !!data),
			)
			.subscribe(({ margins, pageSize }) => {
				this.businessDispatch.SetDefaultPageOptions(margins, pageSize);
			});
	}
}
